import { Alert, Button, HFlow, Theme, useStyles, VFlow } from 'bold-ui'
import { useAlert } from 'components/alert'
import { DateTime } from 'components/date'
import { useErrorHandler } from 'components/error'
import { Form, FormRenderProps, FormValueSpy } from 'components/form'
import { AccordionField } from 'components/form/field/AccordionField'
import { PageContent } from 'components/layout/PageContent'
import { confirm } from 'components/modals/confirm'
import { useCancelarAtendimentoMutation, useEscutaInicialSaveMutation } from 'graphql/hooks.generated'
import React, { CSSProperties, Dispatch, useCallback, useMemo } from 'react'
import { AccordionItem } from 'react-accessible-accordion'
import { FormSpy } from 'react-final-form'
import { useHistory } from 'react-router'

import { DeclaracaoComparecimentoButton } from '../detail/soap/plano/atestados/DeclaracaoComparecimentoBox'
import { SoapSection } from '../detail/soap/SoapSection'
import { convertEscutaInicialModelToInput } from './convert'
import { EscutaInicialState, name } from './model'
import { MotivoConsultaForm } from './motivo-consulta/MotivoConsultaForm'
import { escutaInicialValidator } from './validator'

interface EscutaInicialMainViewProps {
  atendimentoId: ID
  cidadaoId: ID
  initialValues: EscutaInicialState
  updateCache: Dispatch<EscutaInicialState>
  clearCache: Dispatch<void>
}

export function EscutaInicialMainView(props: EscutaInicialMainViewProps) {
  const { atendimentoId, cidadaoId, initialValues, updateCache, clearCache } = props
  const { classes } = useStyles(createStyles)
  const [save] = useEscutaInicialSaveMutation()
  const [cancelar] = useCancelarAtendimentoMutation()
  const history = useHistory()
  const alert = useAlert()
  const handleRejection = useErrorHandler()
  const validators = useMemo(() => escutaInicialValidator, [])

  const handleFormChange = useCallback(
    (values: EscutaInicialState) => {
      updateCache(values)
      values = { ...values, lastSaved: new Date() }
    },
    [updateCache]
  )

  const handleFinalizar = useCallback(
    (values: EscutaInicialState) =>
      save({
        variables: {
          input: convertEscutaInicialModelToInput(atendimentoId, cidadaoId, values),
        },
      })
        .then(() => {
          alert('success', 'Escuta inicial finalizada com sucesso')
          clearCache()
          history.push('/lista-atendimento')
        })
        .catch(handleRejection),
    [alert, atendimentoId, cidadaoId, clearCache, handleRejection, history, save]
  )

  const handleCancelar = useCallback(
    () =>
      confirm({
        title: `Deseja cancelar a escuta inicial?`,
        body: 'As alterações realizadas serão perdidas.',
        confirmLabel: 'Sim',
        cancelLabel: 'Não',
        onConfirm: () => {
          cancelar({ variables: { atendimentoId } })
            .then(() => {
              alert('success', 'Escuta inicial cancelada com sucesso')
              clearCache()
              history.push('/lista-atendimento')
            })
            .catch(handleRejection)
        },
      })(),
    [alert, atendimentoId, cancelar, clearCache, handleRejection, history]
  )

  const renderForm = (formProps: FormRenderProps<EscutaInicialState>) => {
    return (
      <>
        <FormValueSpy<EscutaInicialState> onChange={handleFormChange} />
        <PageContent fluid type='filled'>
          <MotivoConsultaForm name={name.motivoConsulta} />
          <AccordionField allowZeroExpanded name={name.openedAccordions}>
            <VFlow>
              <AccordionItem uuid='D'>
                <SoapSection initial='D' title='Desfecho'>
                  <DeclaracaoComparecimentoButton atendimentoId={atendimentoId} />
                </SoapSection>
              </AccordionItem>
            </VFlow>
          </AccordionField>
        </PageContent>

        <div className={classes.actions}>
          <PageContent fluid type='filled'>
            <HFlow justifyContent='space-between'>
              <FormSpy<EscutaInicialState> subscription={{ dirty: true, active: true, values: true }}>
                {(props) => (
                  <Alert inline type='success' styles={{ wrapper: classes.draftAlertWrapper }}>
                    Rascunho salvo automaticamente às <DateTime format='HH:mm' value={props.values.lastSaved} />
                  </Alert>
                )}
              </FormSpy>
              <HFlow justifyContent='flex-end'>
                <Button kind='normal' onClick={handleCancelar}>
                  Cancelar escuta inicial
                </Button>
                <Button kind='primary' onClick={formProps.handleSubmit}>
                  Finalizar escuta inicial
                </Button>
              </HFlow>
            </HFlow>
          </PageContent>
        </div>
      </>
    )
  }

  return (
    <Form<EscutaInicialState>
      render={renderForm}
      onSubmit={handleFinalizar}
      validate={validators()}
      validateOnBlur
      initialValues={initialValues}
    />
  )
}

const createStyles = (theme: Theme) => ({
  actions: {
    borderTop: `1px solid ${theme.pallete.divider}`,
    height: '6.125rem',
  } as CSSProperties,
  draftAlertWrapper: {
    backgroundColor: 'transparent',
    borderWidth: 0,
  },
})
